<template>
  <div id="progress-modal-parent">
    <Page title="経年データ">
      <template v-slot:content>
        <div class="uk-card uk-card-default uk-card-body" :class="{ 'main-hidden': activePath === 'sub' }">
          <div class="content-header">
            <ActionButton
              title="分析対象追加"
              color="primary"
              width="150"
              @click="onAddButtonClick"
              :uk-tooltip="`title: 分析可能人数は${participantsMin}人〜${participantsMax}人です。; pos: top-left;`"
            />
            <ActionButton title="印刷" color="primary" shape="round" width="150" @click="onPrintButtonClick" />
          </div>
          <div class="content-main">
            <div class="selected-users">
              <UserSelector ref="selector" :users="selectedUsers" :readOnly="true" :selectable="true" @user-click="switchUser" />
            </div>
            <div class="legend">
              <div
                class="legend-items"
                v-for="(chartData, index) in displayChartDatas"
                :key="index"
                v-show="chartData.userId === focusedUser.userId"
              >
                <div class="field">
                  <FieldLabel title="受検日" :required="false" />
                  <Multiselect
                    class="multiselect-blue"
                    v-model="chartData.examDate"
                    v-bind="chartData.examDateOptions"
                    :max="3"
                    placeholder="複数選択"
                    noOptionsText="読込中です"
                    @change="onChangeExamDate"
                  />
                </div>
                <ProgressChart
                  :id="index"
                  :chartData="chartData"
                  :options="chartOptions"
                  :height="120"
                  style="min-width: '900px';"
                  :ref="`chart_${chartData.userId}`"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="uk-flex-top" uk-modal ref="selectionModal" container="#progress-modal-parent" :bg-close="false">
          <div class="uk-modal-dialog uk-margin-auto-vertical">
            <div class="uk-modal-body">
              <DepartmentUserSelector
                :key="departmentUserSelectorFormKey"
                :full="true"
                :analysisFlg="true"
                :initialSelectedUsers="selectedUsers"
                :participantsMax="participantsMax"
                :participantsMin="participantsMin"
                @user-selection-change="onTargetUserSelectionChange"
                @on-selection-done-button-click="userSelect"
                @close="onCloseButtonClick"
                v-on:keydown.esc="onCloseButtonClick"
              />
            </div>
          </div>
        </div>
      </template>
    </Page>
  </div>
</template>
<style src="@vueform/multiselect/themes/default.css"></style>
<style lang="scss" scoped>
@include multiselect;
.field-item {
  margin-top: $spacing-small;
  margin-bottom: $spacing-small;
}
.chart-action-button {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}
.action .action-button:first-child {
  margin-right: $spacing-medium;
}
.uk-card-body {
  .content-main {
    display: flex;
  }
  .content-header {
    display: flex;
    justify-content: space-between;
    margin-bottom: $spacing-ex-small;
  }
}
.selected-users {
  width: 250px;
  min-width: 250px;
  margin-right: $spacing-medium;
  max-height: 500px;
  min-height: 300px;
  .user-selector-container {
    height: 100%;
  }
}
.main-hidden {
  display: none;
}
.uk-modal-dialog {
  width: unset;
  min-width: 960px;
}
.uk-modal-header {
  border-bottom: 0;
}
.legend {
  display: flex;
  flex-flow: column;
  flex-wrap: wrap;
  width: 100%;
}
.multiselect {
  margin: 12px 0;
  width: 36em;
}
</style>

<script lang="ts">
import Page from "../molecules/Page.vue";
import HelpIcon from "../atoms/HelpIcon.vue";
import ActionButton from "../atoms/ActionButton.vue";
import UserSelector from "../molecules/UserSelector.vue";
import ProgressChart from "../molecules/ProgressChart.vue";
import DepartmentUserSelector from "../organisms/DepartmentUserSelector.vue";
import { Options } from "vue-class-component";
import ChartViewMixin from "../../helpers/mixins/chart-view";
import FieldLabel from "../atoms/FieldLabel.vue";
import Multiselect from "@vueform/multiselect";
import { SelectableProfile, GetProfile, UserProgressStats, GetExamDate, GetStat } from "../../types";
import { StatsItemEnum } from "../../autogen";
import { print } from "../../helpers/view-utils";
import { GraphColors } from "../../helpers/const";

export type ChartData = {
  userId: string;
  userName: string;
  examDate: string[];
  examDateOptions: Object;
  datas: GraphData;
};
export type GraphData = {
  labels: string[] | string[][];
  datasets: DataSet[];
};

type DataSet = {
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  data: Object[];
  label: string;
  examDate?: string;
};

@Options({
  components: {
    Page,
    HelpIcon,
    ActionButton,
    ProgressChart,
    UserSelector,
    DepartmentUserSelector,
    FieldLabel,
    Multiselect
  }
})
export default class Progress extends ChartViewMixin {
  participantsMin = 1;
  participantsMax = 100;
  focusedUser: Partial<UserProgressStats> = {};
  selectedUsers: SelectableProfile[] = [];
  stats: (GetExamDate & GetStat & GetProfile)[] = [];
  chartDatas: ChartData[] = []; // 元データ。このデータは取得後は変更しない
  displayChartDatas: ChartData[] = []; // chartDatasから生成してセットする表示用のデータ
  scoreDiffs: number[][] = [];
  chartOptions = {
    interaction: {
      intersect: false,
      mode: "index"
    },
    scales: {
      y: {
        suggestedMin: 10,
        suggestedMax: 90
      }
    },
    plugins: {
      tooltip: {
        intersect: false,
        callbacks: {
          title: (tooltipItems: any) => {
            const title = tooltipItems[0].label.replaceAll(",", "").replaceAll("丨", "ー");
            return title;
          }
        }
      }
    }
  };
  activePath = "main";
  peopleId = "";
  onViewButtonClick() {
    this.activePath = "main";
  }
  onTutorialButtonClick() {
    this.activePath = "sub";
  }
  async userSelect() {
    this.targetStats = [StatsItemEnum.ExamDate, StatsItemEnum.Progress];
    this.onSelectionDoneButtonClick();
  }
  async onSelectionDone() {
    this.changeFocus(this.selectedUsers[0]);
    (this.$refs["selector"] as UserSelector).onUserClick(this.selectedUsers[0]);
    if (this.selectedUsers.length === 0) return;

    this.stats = this.selectedUsers.map(selectedUser =>
      Object.assign(
        this.chartData![selectedUser.id].profile,
        this.chartData![selectedUser.id].stats["ExamDate"],
        { Progress: this.chartData![selectedUser.id].stats["Progress"] },
        {
          id: selectedUser.id
        }
      )
    );
    if (!this.stats) return;
    this.chartDatas = this.stats.map(stat => {
      if (stat.examDates && stat.Progress) {
        const examDates = stat.examDates.map(date => this.convertDateFormat(date));
        const laterDates = stat.examDates
          .sort((a, b) => (a > b ? -1 : 1))
          .slice(0, 3)
          .map(date => this.convertDateFormat(date));

        return {
          userId: stat.id,
          userName: stat.lastName + stat.firstName,
          examDate: laterDates,
          examDateOptions: {
            mode: "tags",
            value: [],
            label: "label",
            trackBy: "value",
            closeOnSelect: false,
            options: examDates.sort((a, b) => (a > b ? -1 : 1)).map(date => ({ value: date, label: `${date}時` })),
            reachable: true,
            createOption: false,
            delay: 0
          },
          datas: {
            labels: stat.Progress[0].score.map(score =>
              score.name
                .replace(/ー/g, "丨")
                .replace("ﾓﾗﾄﾘｱﾑ", "モラトリアム")
                .split("")
            ),
            datasets: stat.Progress.sort((a, b) => (a.date > b.date ? -1 : 1))
              .filter(progress => this.convertDateFormat(progress.date))
              .map((progress, index) => ({
                label: `${this.convertDateFormat(progress.date)}時`,
                data: progress.score.map(score => Math.round(score.value)),
                backgroundColor: GraphColors[index],
                borderColor: GraphColors[index],
                borderWidth: 2,
                examDate: progress.date
              }))
          }
        };
      }
    }) as ChartData[];
    // 元データを表示用の変数にコピー
    this.displayChartDatas = JSON.parse(JSON.stringify(this.chartDatas));
    this.displayChartDatas = this.displayChartDatas.map(data => {
      if (data.datas) {
        return {
          userId: data.userId,
          userName: data.userName,
          examDate: data.examDate,
          examDateOptions: data.examDateOptions,
          datas: {
            labels: data.datas.labels,
            datasets: data.datas.datasets.filter(dataset =>
              data.examDate.map(d => d.replace(/\/|\s+/g, "")).includes(dataset.examDate!)
            )
          }
        };
      }
    }) as ChartData[];
    // 日付表示のために先頭の経年データを取得
    const displayChartData = await this.displayChartDatas.find(d => d.userId === this.focusedUser.id);
    if (!displayChartData) return;
    this.onChangeExamDate(displayChartData.examDate);
  }
  async switchUser(selectedUser: SelectableProfile) {
    this.changeFocus(selectedUser);
  }
  async changeFocus(selectedUser: SelectableProfile) {
    this.focusedUser = selectedUser;
  }
  onChangeExamDate(examDates: string[]) {
    const currentChartData = this.displayChartDatas.find(d => d.userId === this.focusedUser.id);
    // データを抜き出すときは元データから
    const storedChartData = this.chartDatas.find(d => d.userId === this.focusedUser.id);
    if (currentChartData && storedChartData) {
      // 元データから切り替えた日付でフィルタリングしてデータをセットする
      currentChartData.datas.datasets = storedChartData.datas.datasets
        .filter(d => (examDates ? examDates.map(date => date.replace(/\/|\s+/g, "")).includes(d.examDate!) : false))
        .map((dataset, index) => ({
          label: dataset.label,
          data: dataset.data,
          backgroundColor: GraphColors[index],
          borderColor: GraphColors[index],
          borderWidth: 2,
          examDate: dataset.examDate
        }));
    }

    (this.$refs[`chart_${this.focusedUser.id}`] as ProgressChart).rerenderChart();
  }
  onPrintButtonClick() {
    const printTarget = this.displayChartDatas.find(data => data.userId === this.focusedUser.id);
    if (printTarget) {
      print(StatsItemEnum.Progress, printTarget, this.chartOptions);
    }
  }
  /**
   * 文字列を日付のフォーマットに変換する
   * @param str yyyymmddhh の文字列
   * @return yyyy/mm/dd hh のフォーマットに変換した文字列
   */
  convertDateFormat(str: string) {
    return `${str.substr(0, 4)}/${str.substr(4, 2)}/${str.substr(6, 2)} ${str.substr(8, 2)}`;
  }
}
</script>
